/**
 * Mobile Drawer Search Results Component
 *
 * Displays search results in the mobile drawer with highlighting,
 * tags, and proper result formatting.
 */

import React from "react";
import { Tag, ExternalLink } from "lucide-react";
import { SearchResult } from "@wtc/ui";

interface MobileDrawerSearchResultsProps {
  searchResults: SearchResult[];
  renderHighlightedText: (text: string) => React.ReactNode;
  getResultIcon: (iconType: string) => React.ReactNode;
  onResultClick: (result: SearchResult) => void;
  showFullPageLink?: boolean;
  fullPageSearchUrl?: string;
  onClose?: () => void;
}

export function MobileDrawerSearchResults({
  searchResults,
  renderHighlightedText,
  getResultIcon,
  onResultClick,
  showFullPageLink = true,
  fullPageSearchUrl = "/search",
  onClose,
}: MobileDrawerSearchResultsProps) {
  return (
    <div className="w-full">
      {searchResults.length >= 10 && showFullPageLink && (
        <div className="px-4 py-2 border-b border-gray-100">
          <a
            href={fullPageSearchUrl}
            className="inline-flex items-center text-xs text-blue-600 hover:text-blue-800 hover:underline transition-colors"
            onClick={(e) => {
              e.preventDefault();
              onClose?.();
              window.location.href = fullPageSearchUrl;
            }}
          >
            <ExternalLink className="h-3 w-3 mr-1" />
            View all {searchResults.length} results in full search
          </a>
        </div>
      )}
      {searchResults.map((result) => (
        <div
          key={result.objectID}
          onClick={() => onResultClick(result)}
          className="w-full px-4 py-3 hover:bg-card-50 rounded-lg text-left transition-all duration-200 transform active:scale-95 cursor-pointer"
        >
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 mt-0.5">{getResultIcon(result.iconType)}</div>
            <div className="flex-1 min-w-0">
              {/* Title with highlighting */}
              <div className="text-sm font-medium text-card-foreground truncate">
                {result.highlightedTitle
                  ? renderHighlightedText(result.highlightedTitle)
                  : result.title}
              </div>

              {/* Subtitle */}
              {result.subtitle && (
                <p className="text-xs text-gray-500 truncate mt-0.5">{result.subtitle}</p>
              )}

              {/* Highlighted content preview */}
              {result.highlightedContent && (
                <div
                  className="text-xs text-gray-600 dark:text-gray-400 mt-1 overflow-hidden"
                  style={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {renderHighlightedText(result.highlightedContent)}
                </div>
              )}

              {/* Tags */}
              {result.tags && result.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {result.tags.slice(0, 3).map((tag: string, index: number) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200"
                    >
                      <Tag className="h-2.5 w-2.5 mr-1" />
                      {tag}
                    </span>
                  ))}
                  {result.tags.length > 3 && (
                    <span className="text-xs text-gray-400">+{result.tags.length - 3} more</span>
                  )}
                </div>
              )}
            </div>
            <div className="flex-shrink-0">
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-card-100 text-gray-800 dark:text-gray-200 capitalize">
                {result.type.replace("_", " ")}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
